import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  fetchShelves,
  updateShelfAuditFlag,
  resetShelfAudit,
  startShelfAudit,
  endShelfAudit,
  claimShelfAudit,
  releaseShelfAudit,
  claimNextShelfAudit,
} from '../../services/devAPI';
import { searchShelfItems } from '../../services/shelfIndexAPI';
import { LabelCheckOutModal } from '../shared/StorageInfoModal';
import ProductInfoModal from '../shared/ProductInfoModal';
import ShelfAutocompleteInput from '../shared/ShelfAutocompleteInput';
import { getApiErrorMessage } from '../../services/errorUtils';
import { useAuth } from '../../contexts/AuthContext';
import { NavbarPageActions } from '../layout/NavbarPageActions';
import { useShelfValidation } from '../../hooks/useShelfValidation';
import { SHELF_RANGE } from '../../constants/app.constants';
import type { ShelfAuditSession } from '../../types/apiTypes';
import ShelfAuditChip from './ShelfAuditChip';
import ShelfAuditHeightLimit, {
  readStoredShelfAuditHeightLimit,
  storeShelfAuditHeightLimit,
} from './ShelfAuditHeightLimit';
import type { ShelfAuditHeightLimit as ShelfAuditHeightLimitValue } from '@/shared/types/shelfAudit.types';
import type {
  ShelfAuditAdvanceSession,
  ShelfAuditChipShelf,
  ShelfAuditChipItem,
  ShelfAuditChipHandle,
} from './ShelfAuditChip';
import { buildShelfAuditScanTrail } from './shelfAuditScanTrail';
import { extractStorageIdFromQr } from '../orders/orderdetail/scanningLogic';
import { ScannerSection, type ScannerSectionHandle } from '../scanner';
import PickScanBar, { CameraIcon } from '../orders/orderdetail/PickScanBar';
import { cleanOcrText } from './ocrAbScoring';
import '../layout/DevPanel.css';
import '../labels/PrintBoxLabel.css';
import '../warehouse/SearchWarehouse.css';
import '../orders/orderdetail/pick-list.css';
import './ShelfAudit.css';

interface Shelf {
  id: number;
  shelf_name: string;
  shelf_type?: string;
  audit_flag: boolean | null;
  audited_at?: string | null;
  performed_by?: string | null;
  claimed_by?: string | null;
  claim_expires_at?: string | null;
  existing_ids?: string[];
  label_count?: number;
}

function formatAuditedOn(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/** Audit history date, local time on a 12-hour clock. Example: 10/2/26 3:45 PM. */
function formatAuditHistoryDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const year = String(d.getFullYear() % 100).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const meridiem = d.getHours() < 12 ? 'AM' : 'PM';
  const hour12 = d.getHours() % 12 === 0 ? 12 : d.getHours() % 12;
  return `${d.getMonth() + 1}/${d.getDate()}/${year} ${hour12}:${minutes} ${meridiem}`;
}

const SHELF_LOCATION_PATTERN = /\d{1,2}-[A-Z]-\d+/;

/** Shelf-location text from OCR or a shelf QR. Storage-id payloads do not match. */
function matchShelfLocationScan(text: string): string | null {
  const match = cleanOcrText(text).match(SHELF_LOCATION_PATTERN);
  return match ? match[0] : null;
}

function prefillIdsForShelf(shelf: Shelf): string[] | null {
  if (!shelf.audited_at) return null;
  return shelf.existing_ids ?? [];
}

type RecordView = 'history' | 'pending';

interface ShelfItem {
  id: string;
  sku: string;
  type: string;
  shelf_index: string;
}

interface SelectedLabel {
  loc: { id: string; shelf_index: string; type: string };
  sku: string;
  onSuccess: () => Promise<void>;
}

type AdminListFilter = 'all' | 'passed' | 'failed' | 'pending';

const ADMIN_LIST_FILTER_LABELS: Record<AdminListFilter, string> = {
  all: 'View All',
  passed: 'View Passed',
  failed: 'View Failed',
  pending: 'View Pending Audit',
};

function shelfAuditRowClass(auditFlag: boolean | null): string {
  if (auditFlag === true) return 'sa-row-pass';
  if (auditFlag === false) return 'sa-row-fail';
  return '';
}

/** Scan mode opens the label camera straight away; the walk view otherwise starts on the chip list. */
interface OpenClaimedShelfOptions {
  readonly inScanMode: boolean;
}

interface ShelfAuditFlagTableProps {
  readonly shelves: readonly Shelf[];
  readonly countLabel: string;
  readonly onRowClick: (shelf: Shelf) => void;
  /** Omit to hide the pass/fail column (start-page pending list). */
  readonly onToggle?: (event: React.MouseEvent, id: number, nextFlag: boolean | null) => void;
}

/** Same table as admin View Pending Audit: pass/fail marks and shelf name. */
function ShelfAuditFlagTable({
  shelves,
  countLabel,
  onRowClick,
  onToggle,
}: ShelfAuditFlagTableProps): React.ReactElement {
  return (
    <div className="dev-panel-backup-section sa-view-all">
      <p style={{ color: '#aaa', marginBottom: '1rem' }}>{countLabel}</p>
      <table className="dev-panel-backup-table">
        <thead>
          <tr>
            {onToggle && <th style={{ width: 80, textAlign: 'center' }}>Audit</th>}
            <th>Shelf Name</th>
          </tr>
        </thead>
        <tbody>
          {shelves.map((shelf) => (
            <tr
              key={shelf.id}
              className={shelfAuditRowClass(shelf.audit_flag)}
              onClick={() => onRowClick(shelf)}
              style={{ cursor: 'pointer' }}
            >
              {onToggle && (
                <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                  <button
                    className={`sa-toggle-btn sa-toggle-pass ${shelf.audit_flag === true ? 'sa-active' : 'sa-inactive'}`}
                    onClick={(e) => onToggle(e, shelf.id, shelf.audit_flag === true ? null : true)}
                    title="Mark as audited (pass)"
                  >
                    &#10004;
                  </button>
                  <button
                    className={`sa-toggle-btn sa-toggle-fail ${shelf.audit_flag === false ? 'sa-active' : 'sa-inactive'}`}
                    onClick={(e) => onToggle(e, shelf.id, shelf.audit_flag === false ? null : false)}
                    title="Mark as audited (fail)"
                  >
                    &#10008;
                  </button>
                </td>
              )}
              <td className="dev-panel-backup-key">{shelf.shelf_name}</td>
            </tr>
          ))}
          {shelves.length === 0 && (
            <tr>
              <td colSpan={onToggle ? 2 : 1} style={{ color: '#666', textAlign: 'center', padding: '2rem' }}>
                No shelves found
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

const ShelfAudit: React.FC = () => {
  const { user, isAdmin, showAdminUi, toggleShowAdminUi } = useAuth();
  const { validateShelf } = useShelfValidation();
  const [shelves, setShelves] = useState<Shelf[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [resetLoading, setResetLoading] = useState<boolean>(false);
  const [resetSuccess, setResetSuccess] = useState<string | null>(null);
  const [auditActionLoading, setAuditActionLoading] = useState<boolean>(false);
  const [openAudit, setOpenAudit] = useState<ShelfAuditSession | null>(null);
  const [auditStatusKnown, setAuditStatusKnown] = useState(false);
  const [adminListFilter, setAdminListFilter] = useState<AdminListFilter | null>(null);

  const [startShelfName, setStartShelfName] = useState<string>('');
  const [heightLimit, setHeightLimit] = useState<ShelfAuditHeightLimitValue | null>(
    () => readStoredShelfAuditHeightLimit()
  );
  const [showManualShelfInput, setShowManualShelfInput] = useState<boolean>(false);
  const [shelfNumber, setShelfNumber] = useState('');
  const [shelfLetter, setShelfLetter] = useState('');
  const [shelfPosition, setShelfPosition] = useState('');
  const [manualShelfText, setManualShelfText] = useState('');
  const [shelfModalError, setShelfModalError] = useState<string | null>(null);

  const [walkActive, setWalkActive] = useState<boolean>(false);
  const [currentIndex, setCurrentIndex] = useState<number>(-1);
  const [walkComplete, setWalkComplete] = useState<boolean>(false);

  const [chipItems, setChipItems] = useState<ShelfAuditChipItem[]>([]);
  const [chipItemsLoading, setChipItemsLoading] = useState<boolean>(false);
  const [completion, setCompletion] = useState<{ audited: number; total: number; percent: number } | null>(null);
  const [scanOpen, setScanOpen] = useState<boolean>(false);
  const [scanSubmitting, setScanSubmitting] = useState(false);
  const [scanCameraMessage, setScanCameraMessage] = useState<string | null>(null);
  const [scanCameraMessageKey, setScanCameraMessageKey] = useState(0);
  const [locationCameraOpen, setLocationCameraOpen] = useState(true);
  const [locationScanFlash, setLocationScanFlash] = useState(false);
  const [recordView, setRecordView] = useState<RecordView>('history');
  const [scanPrefillIds, setScanPrefillIds] = useState<string[] | null>(null);
  const [startInScanMode, setStartInScanMode] = useState(false);
  const [reviewShelfId, setReviewShelfId] = useState<number | null>(null);
  const [scanVisit, setScanVisit] = useState(0);
  const scannerCaptureRef = useRef<ScannerSectionHandle>(null);
  const locationFlashTimerRef = useRef<number | null>(null);
  const chipHandleRef = useRef<ShelfAuditChipHandle>(null);
  const advanceGenerationRef = useRef(0);
  const advanceInFlightRef = useRef(false);
  const advancePromiseRef = useRef<Promise<ShelfAuditAdvanceSession> | null>(null);
  const chipItemsRequestRef = useRef(0);
  const frontierIdRef = useRef<number | null>(null);
  const reviewShelfIdRef = useRef<number | null>(null);

  const [modalShelf, setModalShelf] = useState<string | null>(null);
  const [modalItems, setModalItems] = useState<ShelfItem[]>([]);
  const [modalLoading, setModalLoading] = useState<boolean>(false);
  const [selectedLabel, setSelectedLabel] = useState<SelectedLabel | null>(null);
  const [productInfoItem, setProductInfoItem] = useState<{ sku: string } | null>(null);

  const numbers1to10 = SHELF_RANGE.NUMBERS_1_TO_10;
  const lettersAtoZ = SHELF_RANGE.LETTERS_A_TO_Z;
  const numbers1to6 = SHELF_RANGE.POSITIONS_1_TO_6;

  const loadChipItems = useCallback(async (shelfName: string) => {
    const requestId = ++chipItemsRequestRef.current;
    setChipItemsLoading(true);
    setChipItems([]);
    try {
      const { items } = await searchShelfItems(shelfName);
      if (requestId !== chipItemsRequestRef.current) return;
      setChipItems(items || []);
    } catch (err: unknown) {
      if (requestId !== chipItemsRequestRef.current) return;
      setError(getApiErrorMessage(err, 'Failed to load shelf contents'));
      setChipItems([]);
    } finally {
      if (requestId === chipItemsRequestRef.current) setChipItemsLoading(false);
    }
  }, []);

  /** If this user already holds a shelf claim, open that shelf in the walk UI. */
  const resumeExistingClaim = useCallback(
    async (
      rows: Shelf[],
      audit: ShelfAuditSession | null,
      email: string | null | undefined
    ): Promise<void> => {
      const normalizedEmail = (email || '').trim().toLowerCase();
      if (!normalizedEmail) return;

      const claimedIdx = rows.findIndex(
        (s) => (s.claimed_by || '').trim().toLowerCase() === normalizedEmail
      );
      if (claimedIdx < 0) return;

      let shelf = rows[claimedIdx];
      if (audit) {
        try {
          const { shelf: claimed } = await claimShelfAudit(shelf.id);
          setShelves((prev) =>
            prev.map((s) => (s.id === claimed.id ? { ...s, ...claimed } : s))
          );
          shelf = { ...shelf, ...claimed };
        } catch {
          // List already shows the claim; still surface the shelf.
        }
      }

      setWalkComplete(false);
      setWalkActive(true);
      setCurrentIndex(claimedIdx);
      setStartShelfName(shelf.shelf_name);
      void loadChipItems(shelf.shelf_name);
    },
    [loadChipItems]
  );

  const loadShelves = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { shelves: rows, completion: nextCompletion, audit } = await fetchShelves();
      // Keep server natural sort (1-A-1 → 1-A-2 → … → 1-B-1)
      setShelves(rows);
      setCompletion(nextCompletion);
      setOpenAudit(audit);
      await resumeExistingClaim(rows, audit, user?.email);
      setAuditStatusKnown(true);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to load shelves'));
    } finally {
      setLoading(false);
    }
  }, [resumeExistingClaim, user?.email]);

  useEffect(() => {
    void loadShelves();
  }, [loadShelves]);

  useEffect(() => {
    return () => {
      if (locationFlashTimerRef.current != null) {
        window.clearTimeout(locationFlashTimerRef.current);
      }
    };
  }, []);

  const refreshCompletion = useCallback(async () => {
    try {
      const { completion: nextCompletion, audit } = await fetchShelves();
      setCompletion(nextCompletion);
      setOpenAudit(audit);
      setAuditStatusKnown(true);
    } catch {
      // Keep last known completion if refresh fails.
    }
  }, []);

  const findShelfIndex = useCallback((shelfName: string): number => {
    const normalized = shelfName.trim().toUpperCase();
    return shelves.findIndex((s) => s.shelf_name.trim().toUpperCase() === normalized);
  }, [shelves]);

  const handleManualShelfInputClose = () => {
    setShowManualShelfInput(false);
    setShelfModalError(null);
  };

  const handleOpenShelfInput = () => {
    setShelfModalError(null);
    if (startShelfName) {
      const parts = startShelfName.split('-');
      if (parts.length === 3) {
        setShelfNumber(parts[0]);
        setShelfLetter(parts[1]);
        setShelfPosition(parts[2]);
        setManualShelfText('');
      } else {
        setShelfNumber('');
        setShelfLetter('');
        setShelfPosition('');
        setManualShelfText(startShelfName);
      }
    } else {
      setShelfNumber('');
      setShelfLetter('');
      setShelfPosition('');
      setManualShelfText('');
    }
    setShowManualShelfInput(true);
  };

  const handleManualShelfInput = () => {
    let normalizedIndex: string;
    if (manualShelfText.trim()) {
      normalizedIndex = manualShelfText.trim().toUpperCase();
    } else if (shelfNumber && shelfLetter && shelfPosition) {
      normalizedIndex = `${shelfNumber}-${shelfLetter}-${shelfPosition}`.toUpperCase();
    } else {
      setShelfModalError('Please select all three fields or enter a shelf name.');
      return;
    }

    if (!validateShelf(normalizedIndex)) {
      setShelfModalError(`Shelf ${normalizedIndex} does not exist`);
      return;
    }

    if (findShelfIndex(normalizedIndex) < 0) {
      setShelfModalError(`Shelf ${normalizedIndex} is not in the audit list`);
      return;
    }

    setShelfModalError(null);
    setStartShelfName(normalizedIndex);
    setShowManualShelfInput(false);
    setShelfNumber('');
    setShelfLetter('');
    setShelfPosition('');
    setManualShelfText('');
  };

  const handleStartWalk = async () => {
    if (!openAudit) {
      setError('No audit in progress. An admin must start an audit first.');
      return;
    }
    if (!startShelfName.trim()) {
      setError('Select a starting shelf');
      return;
    }
    const idx = findShelfIndex(startShelfName);
    if (idx < 0) {
      setError(`Shelf ${startShelfName} is not in the audit list`);
      return;
    }
    const target = shelves[idx];
    try {
      setError(null);
      const { shelf: claimed } = await claimShelfAudit(target.id);
      setShelves((prev) => prev.map((s) => (s.id === claimed.id ? { ...s, ...claimed } : s)));
      setScanPrefillIds(null);
      setStartInScanMode(false);
      setReviewShelfId(null);
      setWalkComplete(false);
      setWalkActive(true);
      setCurrentIndex(idx);
      void loadChipItems(claimed.shelf_name);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to claim shelf'));
    }
  };

  const handleReleaseShelf = async () => {
    const releaseAllClaims = advanceInFlightRef.current;
    const pendingAdvance = advancePromiseRef.current;
    advanceGenerationRef.current += 1;
    advanceInFlightRef.current = false;
    const current = currentIndex >= 0 && currentIndex < shelves.length ? shelves[currentIndex] : null;
    if (releaseAllClaims && pendingAdvance) {
      try {
        await pendingAdvance;
      } catch {
        // The advance records its own error on apply. Release still clears claims.
      }
    }
    try {
      // claim-next already moved the lock onto the prefetched shelf, so a release
      // during that prefetch must drop every claim for this user. Wait for the
      // in-flight claim first so its insert cannot land after this delete.
      await releaseShelfAudit(releaseAllClaims ? undefined : current?.id);
    } catch {
      // Best-effort release; local UI still resets.
    }
    chipItemsRequestRef.current += 1;
    setScanPrefillIds(null);
    setStartInScanMode(false);
    setReviewShelfId(null);
    setScanOpen(false);
    setLocationCameraOpen(true);
    setWalkActive(false);
    setWalkComplete(false);
    setCurrentIndex(-1);
    setChipItems([]);
    setChipItemsLoading(false);
    setError(null);
  };

  const handlePassAndAdvance = (
    updatedShelf: ShelfAuditChipShelf
  ): Promise<ShelfAuditAdvanceSession> => {
    const work = runShelfAdvance(updatedShelf);
    advancePromiseRef.current = work;
    void work.finally(() => {
      if (advancePromiseRef.current === work) advancePromiseRef.current = null;
    });
    return work;

    async function runShelfAdvance(
      shelfToAdvance: ShelfAuditChipShelf
    ): Promise<ShelfAuditAdvanceSession> {
      const generation = ++advanceGenerationRef.current;
      advanceInFlightRef.current = true;
      const isCurrent = (): boolean => generation === advanceGenerationRef.current;

      setShelves((prev) =>
        prev.map((s) =>
          s.id === shelfToAdvance.id
            ? {
                ...s,
                ...shelfToAdvance,
                audited_at: shelfToAdvance.audited_at ?? new Date().toISOString(),
                performed_by: (user?.email || '').trim() || s.performed_by || null,
                existing_ids: shelfToAdvance.existing_ids
                  ? [...shelfToAdvance.existing_ids]
                  : s.existing_ids,
              }
            : s
        )
      );
      void refreshCompletion();

      const reviewingHistory =
        reviewShelfIdRef.current != null &&
        shelfToAdvance.id !== frontierIdRef.current;
      if (reviewingHistory) {
        advanceInFlightRef.current = false;
        return {
          apply: () => {
            if (!isCurrent()) return;
            setScanPrefillIds([...(shelfToAdvance.existing_ids ?? [])]);
            setStartInScanMode(true);
            setScanOpen(true);
            setScanVisit((visit) => visit + 1);
            void loadChipItems(shelfToAdvance.shelf_name);
          },
        };
      }

      const ignored = (): ShelfAuditAdvanceSession => ({
        apply: () => {
          if (generation === advanceGenerationRef.current) advanceInFlightRef.current = false;
        },
      });

      const leaveWalk = (complete: boolean, message: string | null): void => {
        if (!isCurrent()) return;
        advanceInFlightRef.current = false;
        chipItemsRequestRef.current += 1;
        if (message) setError(message);
        setScanPrefillIds(null);
        setStartInScanMode(false);
        setReviewShelfId(null);
        setScanOpen(false);
        setLocationCameraOpen(true);
        setWalkActive(false);
        setWalkComplete(complete);
        setCurrentIndex(-1);
        setChipItems([]);
        setChipItemsLoading(false);
      };

      try {
        const { shelf: next } = await claimNextShelfAudit(shelfToAdvance.id, heightLimit);
        if (!isCurrent()) return ignored();
        if (!next) {
          return { apply: () => leaveWalk(true, null) };
        }

        const nextIdx = shelves.findIndex((s) => s.id === next.id);
        if (nextIdx < 0) {
          return { apply: () => leaveWalk(true, null) };
        }

        let items: ShelfAuditChipItem[] = [];
        let itemsError: string | null = null;
        try {
          const result = await searchShelfItems(next.shelf_name);
          items = result.items || [];
          itemsError = result.error ?? null;
        } catch (err: unknown) {
          items = [];
          itemsError = getApiErrorMessage(err, 'Failed to load shelf contents');
        }

        if (!isCurrent()) return ignored();

        return {
          apply: () => {
            if (!isCurrent()) return;
            advanceInFlightRef.current = false;
            chipItemsRequestRef.current += 1;
            setShelves((prev) => prev.map((s) => (s.id === next.id ? { ...s, ...next } : s)));
            if (itemsError) setError(itemsError);
            setChipItems(items);
            setChipItemsLoading(false);
            setScanPrefillIds(null);
            setStartInScanMode(true);
            setScanOpen(true);
            setReviewShelfId(null);
            setCurrentIndex(nextIdx);
          },
        };
      } catch (err: unknown) {
        const message = getApiErrorMessage(err, 'Failed to claim next shelf');
        if (!isCurrent()) return ignored();
        return { apply: () => leaveWalk(false, message) };
      }
    }
  };

  const refreshChipItems = useCallback(async () => {
    if (currentIndex < 0 || currentIndex >= shelves.length) return;
    await loadChipItems(shelves[currentIndex].shelf_name);
  }, [currentIndex, shelves, loadChipItems]);

  const handleChipLabelClick = useCallback((item: ShelfAuditChipItem) => {
    setSelectedLabel({
      loc: { id: item.id, shelf_index: item.shelf_index, type: item.type },
      sku: item.sku,
      onSuccess: refreshChipItems,
    });
  }, [refreshChipItems]);

  const handleChipSkuClick = useCallback((sku: string) => {
    setProductInfoItem({ sku });
  }, []);

  const handleResetAudit = async () => {
    if (!openAudit) {
      setError('No audit in progress to reset');
      return;
    }
    if (
      !window.confirm(
        `Reset ${openAudit.name}? This clears shelf marks, claims, and events for this audit only. Prior audits are kept.`
      )
    ) {
      return;
    }
    try {
      setResetLoading(true);
      setResetSuccess(null);
      setError(null);
      const { updated } = await resetShelfAudit();
      setShelves((prev) =>
        prev.map((s) => ({
          ...s,
          audit_flag: null,
          audited_at: null,
          performed_by: null,
          existing_ids: [],
          claimed_by: null,
          claim_expires_at: null,
        }))
      );
      setCompletion((prev) => (prev ? { ...prev, audited: 0, percent: 0 } : prev));
      void refreshCompletion();
      setResetSuccess(
        updated === 0
          ? `Cleared ${openAudit.name} events. No shelf marks to clear.`
          : `Cleared ${openAudit.name} events and ${updated} shelf mark${updated === 1 ? '' : 's'}.`
      );
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to reset audit'));
    } finally {
      setResetLoading(false);
    }
  };

  const handleStartAudit = async () => {
    try {
      setAuditActionLoading(true);
      setError(null);
      setResetSuccess(null);
      const { audit } = await startShelfAudit();
      setOpenAudit(audit);
      setResetSuccess(`Started ${audit.name}.`);
      await loadShelves();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to start audit'));
    } finally {
      setAuditActionLoading(false);
    }
  };

  const handleEndAudit = async () => {
    if (!openAudit) return;
    if (
      !window.confirm(
        `End ${openAudit.name}? Events are kept. Shelf marks and claims are not cleared.`
      )
    ) {
      return;
    }
    try {
      setAuditActionLoading(true);
      setError(null);
      setResetSuccess(null);
      if (walkActive) {
        await handleReleaseShelf();
      }
      const { audit } = await endShelfAudit();
      setOpenAudit(null);
      setResetSuccess(`Ended ${audit.name}.`);
      await loadShelves();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to end audit'));
    } finally {
      setAuditActionLoading(false);
    }
  };

  const handleToggle = (e: React.MouseEvent, id: number, newFlag: boolean | null) => {
    e.stopPropagation();
    e.preventDefault();
    const oldFlag = shelves.find((s) => s.id === id)?.audit_flag ?? null;
    setShelves((prev) => prev.map((s) => (s.id === id ? { ...s, audit_flag: newFlag } : s)));
    updateShelfAuditFlag(id, newFlag)
      .then(({ shelf }: { shelf: Shelf }) => {
        setShelves((prev) => prev.map((s) => (
          s.id === shelf.id
            ? {
                ...s,
                ...shelf,
                performed_by: shelf.performed_by ?? s.performed_by ?? null,
                existing_ids: s.existing_ids,
                label_count: s.label_count,
              }
            : s
        )));
      })
      .catch((err: unknown) => {
        setShelves((prev) => prev.map((s) => (s.id === id ? { ...s, audit_flag: oldFlag } : s)));
        setError(getApiErrorMessage(err, 'Failed to update audit flag'));
      });
  };

  const handleRowClick = async (shelfName: string) => {
    setModalShelf(shelfName);
    setModalLoading(true);
    setModalItems([]);
    const { items } = await searchShelfItems(shelfName);
    setModalItems(items || []);
    setModalLoading(false);
  };

  const refreshModalItems = useCallback(async () => {
    if (!modalShelf) return;
    setModalLoading(true);
    const { items } = await searchShelfItems(modalShelf);
    setModalItems(items || []);
    setModalLoading(false);
  }, [modalShelf]);

  const toggleAdminListFilter = (filter: AdminListFilter) => {
    setAdminListFilter((prev) => (prev === filter ? null : filter));
  };

  const filteredAdminShelves = useMemo((): Shelf[] => {
    if (adminListFilter === null) return [];
    if (adminListFilter === 'all') return shelves;
    if (adminListFilter === 'passed') return shelves.filter((s) => s.audit_flag === true);
    if (adminListFilter === 'failed') return shelves.filter((s) => s.audit_flag === false);
    return shelves.filter((s) => s.audit_flag == null);
  }, [shelves, adminListFilter]);

  const adminListCountLabel = useMemo((): string => {
    if (adminListFilter === null) return '';
    const n = filteredAdminShelves.length;
    const noun = n === 1 ? 'shelf' : 'shelves';
    if (adminListFilter === 'all') return `${n} ${noun}`;
    if (adminListFilter === 'passed') return `${n} passed ${noun}`;
    if (adminListFilter === 'failed') return `${n} failed ${noun}`;
    return `${n} pending ${noun}`;
  }, [adminListFilter, filteredAdminShelves.length]);

  const frontierShelf: Shelf | null =
    walkActive && currentIndex >= 0 && currentIndex < shelves.length
      ? shelves[currentIndex]
      : null;
  frontierIdRef.current = frontierShelf?.id ?? null;
  reviewShelfIdRef.current = reviewShelfId;

  const reviewedShelf = reviewShelfId != null
    ? shelves.find((shelf) => shelf.id === reviewShelfId) ?? null
    : null;
  const shownShelf = reviewedShelf ?? frontierShelf;

  const scanTrail = useMemo(() => {
    if (!frontierShelf || !user?.email) return [];
    return buildShelfAuditScanTrail({
      shelves,
      heightLimit,
      performedBy: user.email,
      frontierId: frontierShelf.id,
    });
  }, [frontierShelf, shelves, heightLimit, user?.email]);

  const shownTrailPos = shownShelf
    ? scanTrail.findIndex((shelf) => shelf.id === shownShelf.id)
    : -1;
  const canScanBack = shownTrailPos > 0;
  const canScanForward = shownTrailPos >= 0 && shownTrailPos < scanTrail.length - 1;

  const openScannedShelf = (shelfId: number): void => {
    const shelf = shelves.find((row) => row.id === shelfId);
    const frontierId = frontierIdRef.current;
    if (!shelf || frontierId == null) return;
    setReviewShelfId(shelfId === frontierId ? null : shelfId);
    setScanPrefillIds(prefillIdsForShelf(shelf));
    setStartInScanMode(true);
    setScanOpen(true);
    setScanVisit((visit) => visit + 1);
    void loadChipItems(shelf.shelf_name);
  };

  const currentShelf: ShelfAuditChipShelf | null = shownShelf
    ? {
        id: shownShelf.id,
        shelf_name: shownShelf.shelf_name,
        audit_flag: shownShelf.audit_flag,
        audited_at: shownShelf.audited_at ?? null,
      }
    : null;

  const historyShelves = useMemo((): Shelf[] => {
    const email = (user?.email || '').trim().toLowerCase();
    if (!email) return [];
    return shelves
      .filter((shelf) => (shelf.performed_by || '').trim().toLowerCase() === email && shelf.audited_at)
      .sort((a, b) => (b.audited_at || '').localeCompare(a.audited_at || ''));
  }, [shelves, user?.email]);

  const pendingShelves = useMemo(
    (): Shelf[] => shelves.filter((shelf) => shelf.audit_flag == null),
    [shelves],
  );

  const pendingCountLabel = `${pendingShelves.length} pending ${pendingShelves.length === 1 ? 'shelf' : 'shelves'}`;

  const handleScanSubmittingChange = useCallback((submitting: boolean): void => {
    setScanSubmitting(submitting);
  }, []);

  const showScanCameraMessage = useCallback((message: string | null): void => {
    setScanCameraMessage(message);
    if (message) setScanCameraMessageKey((key) => key + 1);
  }, []);

  const flashLocationSuccess = useCallback((): void => {
    if (locationFlashTimerRef.current != null) {
      window.clearTimeout(locationFlashTimerRef.current);
    }
    setLocationScanFlash(true);
    locationFlashTimerRef.current = window.setTimeout(() => {
      locationFlashTimerRef.current = null;
      setLocationScanFlash(false);
    }, 1000);
  }, []);

  const handleLocationCapture = useCallback((data: string | null): boolean => {
    if (!data || !data.trim()) return false;
    const shelfName = matchShelfLocationScan(data);
    if (!shelfName) {
      if (!extractStorageIdFromQr(data)) setError('Scan a shelf location');
      return false;
    }
    if (!validateShelf(shelfName) || findShelfIndex(shelfName) < 0) {
      setError(`Shelf ${shelfName} is not in the audit list`);
      return false;
    }
    setError(null);
    setStartShelfName(shelfName);
    setLocationCameraOpen(false);
    flashLocationSuccess();
    return true;
  }, [findShelfIndex, flashLocationSuccess, validateShelf]);

  const openClaimedShelf = useCallback(async (
    shelf: Shelf,
    options: OpenClaimedShelfOptions = { inScanMode: true },
  ): Promise<void> => {
    if (!openAudit) {
      setError('No audit in progress. An admin must start an audit first.');
      return;
    }
    const idx = shelves.findIndex((row) => row.id === shelf.id);
    if (idx < 0) {
      setError(`Shelf ${shelf.shelf_name} is not in the audit list`);
      return;
    }
    try {
      setError(null);
      const { shelf: claimed } = await claimShelfAudit(shelf.id);
      const merged: Shelf = {
        ...shelf,
        ...claimed,
        existing_ids: claimed.existing_ids ?? shelf.existing_ids,
        label_count: claimed.label_count ?? shelf.label_count,
        performed_by: claimed.performed_by ?? shelf.performed_by ?? null,
      };
      setShelves((prev) => prev.map((row) => (row.id === merged.id ? { ...row, ...merged } : row)));
      setScanPrefillIds(prefillIdsForShelf(merged));
      setStartInScanMode(options.inScanMode);
      setReviewShelfId(null);
      setWalkComplete(false);
      setWalkActive(true);
      setCurrentIndex(idx);
      setScanOpen(options.inScanMode);
      if (options.inScanMode) setScanVisit((visit) => visit + 1);
      void loadChipItems(merged.shelf_name);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to claim shelf'));
    }
  }, [loadChipItems, openAudit, shelves]);

  const selectedStartShelf = useMemo((): Shelf | null => {
    if (!startShelfName.trim()) return null;
    const idx = findShelfIndex(startShelfName);
    return idx >= 0 ? shelves[idx] : null;
  }, [startShelfName, shelves, findShelfIndex]);

  const cameraSheetOpen = walkActive ? scanOpen : locationCameraOpen;

  return (
    <div className={`dev-panel-container sa-page${adminListFilter !== null ? ' sa-list-open' : ''}${currentShelf != null && adminListFilter === null ? ' sa-walk-locked' : ''}${cameraSheetOpen ? ' sa-camera-open' : ''}`}>
      {locationScanFlash && (
        <div className="success-flash">
          <div className="success-message">✓ Success!</div>
        </div>
      )}
      {isAdmin && (
        <NavbarPageActions>
          <button
            type="button"
            className={`sa-admin-toggle${showAdminUi ? ' active' : ''}`}
            onClick={toggleShowAdminUi}
            aria-pressed={showAdminUi}
            title={showAdminUi ? 'Hide admin UI (normal user view)' : 'Show admin UI'}
          >
            Admin
          </button>
        </NavbarPageActions>
      )}

      {showAdminUi && (
        <div className="sa-config-panel">
          <h3 className="dev-panel-section-title">Admin</h3>
          <div className="sa-audit-status">
            {auditStatusKnown && (openAudit ? (
              <span>
                In progress: <strong>{openAudit.name}</strong>
              </span>
            ) : (
              <span>No audit in progress</span>
            ))}
          </div>
          {completion && (
            <div className="sa-completion">
              <span className="sa-completion-percent">{completion.percent}%</span>
              <span className="sa-completion-detail">
                {completion.audited} / {completion.total} shelves audited
              </span>
            </div>
          )}
          <div className="sa-config-actions">
            {auditStatusKnown && (openAudit ? (
              <button
                type="button"
                className="dev-panel-button"
                onClick={() => { void handleEndAudit(); }}
                disabled={auditActionLoading || resetLoading}
              >
                {auditActionLoading ? 'Ending...' : 'End Audit'}
              </button>
            ) : (
              <button
                type="button"
                className="dev-panel-button"
                onClick={() => { void handleStartAudit(); }}
                disabled={auditActionLoading || resetLoading}
              >
                {auditActionLoading ? 'Starting...' : 'Start Audit'}
              </button>
            ))}
            <button
              type="button"
              className="dev-panel-button dev-panel-button-reset"
              onClick={() => { void handleResetAudit(); }}
              disabled={resetLoading || auditActionLoading || !openAudit}
            >
              {resetLoading ? 'Resetting...' : 'Reset Audit'}
            </button>
            {(Object.keys(ADMIN_LIST_FILTER_LABELS) as AdminListFilter[]).map((filter) => (
              <button
                key={filter}
                type="button"
                className={`dev-panel-button${adminListFilter === filter ? ' sa-filter-active' : ''}`}
                onClick={() => toggleAdminListFilter(filter)}
              >
                {adminListFilter === filter && filter === 'all'
                  ? 'Hide All'
                  : ADMIN_LIST_FILTER_LABELS[filter]}
              </button>
            ))}
          </div>
          {resetSuccess && <div className="dev-panel-success">{resetSuccess}</div>}
        </div>
      )}

      {!walkActive && auditStatusKnown && (
        <div className="sa-config-panel sa-user-config">
          <h3 className="dev-panel-section-title">Start shelf</h3>
          {!openAudit && (
            <p className="sa-no-audit-hint">No audit in progress. Ask an admin to start one.</p>
          )}
          <div className="pbl-info-display">
            <div className="pbl-info-row">
              <span className="pbl-info-label">Shelf:</span>
              <div className="pbl-info-value-wrapper">
                <span
                  className={`pbl-info-value pbl-info-clickable ${!startShelfName ? 'pbl-info-empty' : ''}`}
                  onClick={handleOpenShelfInput}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleOpenShelfInput();
                    }
                  }}
                >
                  {startShelfName || 'Scan or enter manually'}
                </span>
                {selectedStartShelf?.audited_at && (
                  <span className="sa-audited-chip">
                    Audited on: {formatAuditedOn(selectedStartShelf.audited_at)}
                  </span>
                )}
                {startShelfName && (
                  <button
                    type="button"
                    className="pbl-info-clear"
                    onClick={(e) => {
                      e.stopPropagation();
                      setStartShelfName('');
                    }}
                    title="Clear Shelf"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          </div>
          <ShelfAuditHeightLimit
            value={heightLimit}
            onChange={(next) => {
              setHeightLimit(next);
              storeShelfAuditHeightLimit(next);
            }}
          />
          <button
            type="button"
            className="dev-panel-button sa-start-button"
            onClick={() => { void handleStartWalk(); }}
            disabled={loading || shelves.length === 0 || !startShelfName || !openAudit}
          >
            Start
          </button>
          <button
            type="button"
            className="dev-panel-button"
            onClick={() => setRecordView((view) => (view === 'history' ? 'pending' : 'history'))}
          >
            {recordView === 'history' ? 'Show Pending Shelves' : 'Show Audit History'}
          </button>
          <div className="sa-record-block">
            <div className="pbl-section-title pbl-section-info sa-record-heading">
              <span className="pbl-section-text">
                {recordView === 'pending' ? 'Pending Shelves' : 'Audit History'}
              </span>
            </div>
            {recordView === 'pending' ? (
              <ShelfAuditFlagTable
                shelves={pendingShelves}
                countLabel={pendingCountLabel}
                onRowClick={(shelf) => { void openClaimedShelf(shelf, { inScanMode: false }); }}
              />
            ) : historyShelves.length === 0 ? (
              <div className="sw-results-empty">No shelves audited yet</div>
            ) : (
              <div className="sw-results-section sa-history-table">
                <div className="sw-table-scroll">
                  <table className="sw-results-table sw-results-id-type-shelf">
                    <thead>
                      <tr>
                        <th>Shelf index</th>
                        <th>SKU count</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historyShelves.map((shelf) => (
                        <tr
                          key={shelf.id}
                          className="sw-results-row-clickable"
                          onClick={() => { void openClaimedShelf(shelf); }}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              void openClaimedShelf(shelf);
                            }
                          }}
                        >
                          <td>{shelf.shelf_name}</td>
                          <td>{(shelf.existing_ids ?? []).length}</td>
                          <td>{shelf.audited_at ? formatAuditHistoryDate(shelf.audited_at) : ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {error && <div className="dev-panel-error">{error}</div>}

      {loading ? (
        <p style={{ color: '#ccc' }}>Loading shelves...</p>
      ) : (
        <div className="sa-walk-stage">
          {walkComplete && (
            <div className="dev-panel-success">Audit walk complete.</div>
          )}

          {currentShelf && (
            <ShelfAuditChip
              key={`${currentShelf.id}:${scanVisit}`}
              ref={chipHandleRef}
              shelf={currentShelf}
              items={chipItems}
              itemsLoading={chipItemsLoading}
              startInScanMode={startInScanMode}
              initialExistingIds={scanPrefillIds}
              canScanBack={canScanBack}
              canScanForward={canScanForward}
              onScanBack={() => {
                const previous = scanTrail[shownTrailPos - 1];
                if (previous) openScannedShelf(previous.id);
              }}
              onScanForward={() => {
                const next = scanTrail[shownTrailPos + 1];
                if (next) openScannedShelf(next.id);
              }}
              onPassAndAdvance={handlePassAndAdvance}
              onError={setError}
              onLabelClick={handleChipLabelClick}
              onSkuClick={handleChipSkuClick}
              onScanModeChange={setScanOpen}
              onScanCameraMessage={showScanCameraMessage}
              onSubmittingChange={handleScanSubmittingChange}
            />
          )}

          {showAdminUi && adminListFilter !== null && (
            <ShelfAuditFlagTable
              shelves={filteredAdminShelves}
              countLabel={adminListCountLabel}
              onRowClick={(shelf) => { void handleRowClick(shelf.shelf_name); }}
              onToggle={handleToggle}
            />
          )}
        </div>
      )}

      {modalShelf && (
        <div className="pbl-modal-overlay" onClick={() => setModalShelf(null)}>
          <div className="pbl-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="pbl-modal-close" onClick={() => setModalShelf(null)} aria-label="Close">&times;</button>
            <div className="pbl-modal-title">Shelf Contents &mdash; {modalShelf}</div>
            {modalLoading ? (
              <div className="sw-results-empty">Loading...</div>
            ) : modalItems.length === 0 ? (
              <div className="sw-results-empty">No items found</div>
            ) : (
              <div className="sw-table-scroll">
              <table className="sw-results-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>SKU</th>
                    <th>Type</th>
                    <th>Index</th>
                  </tr>
                </thead>
                <tbody>
                  {modalItems.map((item, idx) => (
                    <tr key={`${item.sku}-${item.id}-${idx}`}>
                      <td>
                        <button
                          type="button"
                          className="sw-results-id-clickable"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedLabel({
                              loc: { id: item.id, shelf_index: item.shelf_index, type: item.type },
                              sku: item.sku,
                              onSuccess: refreshModalItems
                            });
                          }}
                        >
                          {item.id}
                        </button>
                      </td>
                      <td>{item.sku}</td>
                      <td className="sw-results-type-cell">{item.type}</td>
                      <td className="sw-results-index-cell">{item.shelf_index}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </div>
        </div>
      )}

      {selectedLabel && (
        <LabelCheckOutModal
          loc={selectedLabel.loc}
          sku={selectedLabel.sku}
          onClose={() => setSelectedLabel(null)}
          onSuccess={selectedLabel.onSuccess}
        />
      )}

      {productInfoItem && (
        <ProductInfoModal
          item={productInfoItem}
          onClose={() => setProductInfoItem(null)}
        />
      )}

      <PickScanBar
        open={cameraSheetOpen}
        shutterDisabled={walkActive && scanSubmitting}
        onShutter={() => {
          if (scanSubmitting) return;
          scannerCaptureRef.current?.capture();
        }}
        leftActions={
          walkActive ? (
            <span className="sa-bottom-bar-audit-name">{openAudit?.name ?? 'Audit'}</span>
          ) : null
        }
        centerActions={
          walkActive ? (
            <button
              type="button"
              className="order-detail-bar-camera-btn order-detail-bar-camera-btn-center"
              onClick={() => chipHandleRef.current?.enterScanMode()}
              disabled={chipItemsLoading}
              aria-label="Scan"
            >
              <CameraIcon size={28} />
            </button>
          ) : (
            <button
              type="button"
              className="order-detail-bar-camera-btn order-detail-bar-camera-btn-center"
              onClick={() => setLocationCameraOpen(true)}
              aria-label="Open camera"
            >
              <CameraIcon size={28} />
            </button>
          )
        }
        rightActions={
          walkActive ? (
            <button
              type="button"
              className="dev-panel-button"
              onClick={() => { void handleReleaseShelf(); }}
            >
              Release Shelf
            </button>
          ) : null
        }
      >
        {walkActive ? (
          <ScannerSection
            ref={scannerCaptureRef}
            mode="qr"
            onCapture={(data) => chipHandleRef.current?.ingestScan(data)}
            webcamOn={scanOpen}
            setWebcamOn={(on) => {
              if (!on && scanSubmitting) return;
              setScanOpen(on);
            }}
            className="webcam-capture-container"
            hideReticle={scanSubmitting}
            overlay={
              scanCameraMessage ? (
                <div
                  key={scanCameraMessageKey}
                  className="order-detail-message order-detail-message-error"
                  role="alert"
                >
                  {scanCameraMessage}
                </div>
              ) : undefined
            }
          />
        ) : (
          <ScannerSection
            ref={scannerCaptureRef}
            mode="hybrid"
            onCapture={handleLocationCapture}
            webcamOn={locationCameraOpen}
            setWebcamOn={setLocationCameraOpen}
            className="webcam-capture-container"
            cornerAction={
              <button
                type="button"
                className="webcam-viewport-close-btn"
                onClick={(event) => {
                  event.stopPropagation();
                  setLocationCameraOpen(false);
                }}
                aria-label="Close camera"
              >
                ×
              </button>
            }
          />
        )}
      </PickScanBar>

      {showManualShelfInput && (
        <div className="pbl-modal-overlay" onClick={handleManualShelfInputClose}>
          <div className="pbl-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="pbl-modal-close"
              onClick={handleManualShelfInputClose}
              aria-label="Close"
            >
              ×
            </button>
            <div className="pbl-modal-title">Enter Shelf Index</div>
            <div className="pbl-manual-popup">
              <div className="pbl-dropdown-container">
                <select
                  value={shelfNumber}
                  onChange={(e) => { setShelfNumber(e.target.value); setManualShelfText(''); }}
                  className="pbl-dropdown"
                >
                  <option value="">--</option>
                  {numbers1to10.map((num) => (
                    <option key={num} value={num}>{num}</option>
                  ))}
                </select>
                <span className="pbl-separator">-</span>
                <select
                  value={shelfLetter}
                  onChange={(e) => { setShelfLetter(e.target.value); setManualShelfText(''); }}
                  className="pbl-dropdown"
                >
                  <option value="">--</option>
                  {lettersAtoZ.map((letter) => (
                    <option key={letter} value={letter}>{letter}</option>
                  ))}
                </select>
                <span className="pbl-separator">-</span>
                <select
                  value={shelfPosition}
                  onChange={(e) => { setShelfPosition(e.target.value); setManualShelfText(''); }}
                  className="pbl-dropdown"
                >
                  <option value="">--</option>
                  {numbers1to6.map((num) => (
                    <option key={num} value={num}>{num}</option>
                  ))}
                </select>
              </div>
              <div className="pbl-or-divider">or</div>
              <ShelfAutocompleteInput
                value={manualShelfText}
                onChange={(val) => { setManualShelfText(val); setShelfNumber(''); setShelfLetter(''); setShelfPosition(''); }}
                onSelect={(shelf) => { setManualShelfText(shelf); setShelfNumber(''); setShelfLetter(''); setShelfPosition(''); }}
                placeholder="Type shelf name"
              />
              {shelfModalError && (
                <div className="pbl-message" style={{ color: '#ff6b6b' }}>{shelfModalError}</div>
              )}
              <button
                type="button"
                onClick={handleManualShelfInput}
                className="pbl-submit-button"
                disabled={(!shelfNumber || !shelfLetter || !shelfPosition) && !manualShelfText.trim()}
              >
                Set Shelf Index
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShelfAudit;
